/**
 * Planning Checklist Section Component
 * 
 * Displays planning checklist items with auto-population from bookings, documents, etc.
 */

import { View, Pressable } from 'react-native';
import { useState } from 'react';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ThemedText, Body, Caption } from '@/components/ui/Text';
import { ChecklistItemRow } from './ChecklistItemRow';
import type { ChecklistItem } from '@/services/checklists';

type PlanningChecklistSectionProps = {
  items: ChecklistItem[];
  onToggleItem: (itemId: string) => void;
  onAddItem?: () => void;
  onEditItem?: (item: ChecklistItem) => void;
  loading?: boolean;
  cityContextPlaceLabel?: string;
  cityContextPlanningSuggestions?: Array<{ title: string; notes?: string }>;
  onAddCityContextPlanningSuggestions?: () => void;
  missingCityContextPlanningSuggestionsCount?: number;
};

export function PlanningChecklistSection({
  items,
  onToggleItem,
  onAddItem,
  onEditItem,
  loading = false,
  cityContextPlaceLabel,
  cityContextPlanningSuggestions = [],
  onAddCityContextPlanningSuggestions,
  missingCityContextPlanningSuggestionsCount = 0,
}: PlanningChecklistSectionProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const sectionTitleStyle = { fontSize: 18, lineHeight: 22 } as const;

  const planningItems = items.filter(item => item.group === 'planning' || !item.group);
  const completedCount = planningItems.filter(item => item.completed).length;
  const totalCount = planningItems.length;
  const progress = totalCount > 0 ? completedCount / totalCount : 0;

  if (loading) {
    return (
      <Card variant="outlined">
        <View style={{ padding: theme.spacing(2) }}>
          <Body color={theme.colors.textSecondary}>Loading checklist...</Body>
        </View>
      </Card>
    );
  }

  if (totalCount === 0) {
    return (
      <Card variant="outlined">
        <View style={{ padding: theme.spacing(2), gap: theme.spacing(1) }}>
          <ThemedText variant="bodyStrong" style={sectionTitleStyle}>
            Planning Checklist
          </ThemedText>
          <Body color={theme.colors.textSecondary}>
            No planning items yet. Items will be auto-generated based on your trip details.
          </Body>
          {onAddItem && (
            <Button
              label="Add Item"
              variant="secondary"
              onPress={onAddItem}
              icon={<Ionicons name="add-outline" size={18} color={theme.colors.text} />}
            />
          )}
        </View>
      </Card>
    );
  }

  return (
    <Card variant="outlined" padding={theme.spacing(2)}>
      <View style={{ gap: theme.spacing(1.5) }}>
        <Pressable
          onPress={() => setExpanded(!expanded)}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
              <View
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  backgroundColor: theme.colors.primary + '15',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Ionicons
                  name="checkmark-circle-outline"
                  size={16}
                  color={theme.colors.primary}
                />
              </View>
              <ThemedText variant="bodyStrong" style={sectionTitleStyle}>
                Planning Checklist
              </ThemedText>
            </View>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(1),
                marginTop: theme.spacing(0.5),
              }}
            >
              <Caption color={theme.colors.textSecondary}>
                {completedCount} of {totalCount} completed
              </Caption>
              <Caption color={theme.colors.textSecondary}>
                {Math.round(progress * 100)}%
              </Caption>
            </View>
          </View>
          <Ionicons
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={20}
            color={theme.colors.textSecondary}
          />
        </Pressable>

        {expanded && (
          <>
            <View
              style={{
                height: 3,
                backgroundColor: theme.colors.borderMuted,
                borderRadius: theme.radii.sm,
                overflow: 'hidden',
              }}
            >
              <View
                style={{
                  height: '100%',
                  width: `${progress * 100}%`,
                  backgroundColor: theme.colors.primary,
                }}
              />
            </View>

            {cityContextPlanningSuggestions.length > 0 &&
              missingCityContextPlanningSuggestionsCount > 0 &&
              onAddCityContextPlanningSuggestions && (
                <Card
                  variant="outlined"
                  style={{
                    padding: theme.spacing(1.5),
                    borderRadius: theme.radii.md,
                    backgroundColor: theme.colors.primary + '08',
                    borderColor: theme.colors.borderMuted,
                  }}
                >
                  <View style={{ gap: theme.spacing(1) }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
                      <Ionicons name="location-outline" size={18} color={theme.colors.primary} />
                      <ThemedText variant="bodyStrong">City-specific recommendations</ThemedText>
                    </View>

                    <Caption color={theme.colors.textSecondary}>
                      {cityContextPlaceLabel
                        ? `Planning tips based on local context in ${cityContextPlaceLabel}.`
                        : 'Planning tips based on your destination.'}
                    </Caption>

                    <View style={{ gap: theme.spacing(0.5) }}>
                      {cityContextPlanningSuggestions.slice(0, 6).map((s) => (
                        <View
                          key={s.title}
                          style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(0.75) }}
                        >
                          <Ionicons name="checkmark-circle-outline" size={16} color={theme.colors.primary} />
                          <View style={{ flex: 1 }}>
                            <ThemedText variant="bodyStrong" style={{ fontSize: 13 }}>
                              {s.title}
                            </ThemedText>
                            {s.notes ? (
                              <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                                {s.notes}
                              </Caption>
                            ) : null}
                          </View>
                        </View>
                      ))}
                    </View>

                    <Button
                      label={`Add suggested items (${missingCityContextPlanningSuggestionsCount})`}
                      variant="secondary"
                      size="small"
                      onPress={onAddCityContextPlanningSuggestions}
                      icon={<Ionicons name="add-outline" size={18} color={theme.colors.text} />}
                      accessibilityLabel="Add city-specific planning recommendations"
                      accessibilityHint="Adds suggested planning tasks to your checklist"
                    />
                  </View>
                </Card>
              )}

            <View style={{ gap: theme.spacing(1.5) }}>
              {planningItems.map((item) => (
                <ChecklistItemRow
                  key={item.id}
                  item={item}
                  onToggle={onToggleItem}
                  onPress={onEditItem}
                />
              ))}
            </View>

            {onAddItem && (
              <Button
                label="Add Custom Item"
                variant="secondary"
                onPress={onAddItem}
                icon={<Ionicons name="add-outline" size={18} color={theme.colors.text} />}
              />
            )}
          </>
        )}
      </View>
    </Card>
  );
}

